import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subject, takeUntil} from 'rxjs';
import {CommonModule, DatePipe} from '@angular/common';
import {Router, RouterLink} from '@angular/router';
import {PatientModel} from '../../../models/patient/patient.model';
import {ReservedAppointmentsModel} from '../../../models/appointment/appointment.model';
import {PatientService} from '../../../services/patient/patient.service';
import {AuthenticationService} from '../../../services/authentication/authentication.service';
import {iResponse, iResponseList} from '../../../models/response/response.model';
import {MatTabsModule} from '@angular/material/tabs';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmDialogComponent} from '../../confirm-dialog/confirm-dialog.component';
import {AppointmentService} from '../../../services/appointment/appointment.service';
import {InfoMessageService} from '../../../services/infomessage/infomessage.service';

@Component({
    selector: 'app-appointment',
    standalone: true,
    imports: [CommonModule, DatePipe, RouterLink, MatTabsModule],
    templateUrl: './appointment.component.html',
    styleUrl: './appointment.component.sass'
})
export class AppointmentComponent implements OnInit, OnDestroy {
    private destroy$: Subject<void> = new Subject<void>();
    upcomingAppointments: ReservedAppointmentsModel[] = [];
    pastAppointments: ReservedAppointmentsModel[] = [];
    loggedInPatient: PatientModel | null = null;

    constructor(
        private patientService: PatientService,
        private authenticationService: AuthenticationService,
        private router: Router,
        private appointmentService: AppointmentService,
        private dialog: MatDialog,
        private infoMessageService: InfoMessageService
    ) {}

    ngOnInit() {
        this.getMe();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    cancelAppointment(appointment: ReservedAppointmentsModel) {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                header: 'Termin absagen',
                text: 'Möchten Sie diesen Termin wirklich absagen?',
                delete: true
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.appointmentService.patientCancelAppointment(appointment.id, this.loggedInPatient?.id || '', 'Vom Patienten abgesagt')
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: () => {
                            this.infoMessageService.createMessage('Termin wurde erfolgreich abgesagt', 'success');
                            this.getAppointments();
                        },
                        error: () => {
                            this.infoMessageService.createMessage('Fehler beim Absagen des Termins', 'error');
                        }
                    });
            }
        });
    }

    toDetails(appointment: ReservedAppointmentsModel) {
        this.router.navigate(['/termine', appointment.id], { state: { appointment: appointment} });
    }

    private getMe(): void {
        this.patientService.getMe()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponse<PatientModel>) => {
                    this.loggedInPatient = response.data;
                    this.getAppointments();
                }
            });
    }

    private getAppointments(): void {
        if (!this.loggedInPatient) return;
        this.patientService.getPatientAppointments(this.loggedInPatient.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponseList<ReservedAppointmentsModel>) => {
                    const now = new Date();
                    const allAppointments = response.data;

                    this.upcomingAppointments = allAppointments
                        .filter(a => new Date(a.from) >= now)
                        .sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime());

                    this.pastAppointments = allAppointments
                        .filter(a => new Date(a.from) < now)
                        .sort((a, b) => new Date(b.from).getTime() - new Date(a.from).getTime());
                }
            });
    }
}
